import React, { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Crown, Loader2, CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GrantAccessPanel — UI admin para otorgar acceso promocional/premium temporal
 * con vencimiento automático.
 */
export default function GrantAccessPanel() {
    const [email, setEmail] = useState('');
    const [days, setDays] = useState(60);
    const [planTipo, setPlanTipo] = useState('promocional');
    const [nota, setNota] = useState('Acceso promocional otorgado por Acontista Educa');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return toast.error('Ingresa un email');
        if (days <= 0 || days > 730) return toast.error('Days debe estar entre 1 y 730');
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post('/admin/users/grant-access', {
                email: email.trim(),
                days: Number(days),
                plan_tipo: planTipo,
                nota: nota || null,
            });
            setResult({ ok: true, data });
            toast.success(`Acceso otorgado a ${email}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message;
            setResult({ ok: false, error: msg });
            toast.error(`No se pudo otorgar acceso: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const revoke = async () => {
        if (!email.trim()) return toast.error('Ingresa un email para revocar');
        if (!window.confirm(`¿Revocar el acceso premium de ${email}?`)) return;
        setLoading(true);
        try {
            await api.post(`/admin/users/revoke-access?email=${encodeURIComponent(email.trim())}`);
            toast.success(`Acceso revocado para ${email}`);
            setResult(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    const reenviarAlAdmin = async () => {
        if (!result?.data) return;
        setLoading(true);
        try {
            const fi = new Date(result.data.otorgado_en).toLocaleDateString('es-CL');
            await api.post('/admin/email/test', {
                to: 'acontistaeduca@gmail.com',
                template: 'grant_access',
            });
            toast.success(
                `Copia enviada a acontistaeduca@gmail.com. Reenvíala a ${result.data.email} desde tu Gmail.`,
                { duration: 8000 }
            );
        } catch (err) {
            toast.error('No se pudo enviar la copia: ' + (err?.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50/60 via-white to-white" data-testid="grant-access-panel">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading text-base text-slate-900">
                    <Crown className="h-5 w-5 text-amber-700" />
                    Otorgar acceso promocional / premium temporal
                </CardTitle>
                <p className="text-xs text-slate-500">
                    El sistema desactiva automáticamente el plan al llegar a la fecha de vencimiento. No requiere intervención manual.
                </p>
            </CardHeader>
            <CardContent>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <Label className="text-xs">Email del docente</Label>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="ninoska.lillo.a.369@gmail.com"
                            data-testid="grant-email-input"
                            disabled={loading}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Duración (días)</Label>
                        <Input
                            type="number"
                            min={1}
                            max={730}
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            data-testid="grant-days-input"
                            disabled={loading}
                            className="mt-1 font-mono"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Tipo de plan</Label>
                        <select
                            value={planTipo}
                            onChange={(e) => setPlanTipo(e.target.value)}
                            disabled={loading}
                            data-testid="grant-tipo-select"
                            className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        >
                            <option value="promocional">Promocional</option>
                            <option value="premium_temporal">Premium Temporal</option>
                            <option value="cortesía">Cortesía</option>
                            <option value="pagado">Pagado</option>
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-xs">Nota interna (opcional)</Label>
                        <Input
                            value={nota}
                            onChange={(e) => setNota(e.target.value)}
                            data-testid="grant-nota-input"
                            disabled={loading}
                            className="mt-1"
                        />
                    </div>
                    <div className="sm:col-span-2 flex flex-wrap gap-2 pt-2">
                        <Button type="submit" disabled={loading} className="gap-2" data-testid="grant-submit-btn">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                            Otorgar acceso
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={revoke}
                            disabled={loading || !email}
                            className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
                            data-testid="grant-revoke-btn"
                        >
                            Revocar acceso
                        </Button>
                    </div>
                </form>

                {result?.ok && (
                    <div
                        className={`mt-5 rounded-xl border-2 p-4 space-y-2 ${
                            result.data.email_notificacion?.sent
                                ? 'border-emerald-200 bg-emerald-50'
                                : 'border-amber-300 bg-amber-50'
                        }`}
                        data-testid="grant-result-ok"
                    >
                        <div
                            className={`flex items-center gap-2 text-sm font-semibold ${
                                result.data.email_notificacion?.sent ? 'text-emerald-900' : 'text-amber-900'
                            }`}
                        >
                            {result.data.email_notificacion?.sent ? (
                                <CheckCircle2 className="h-4 w-4" />
                            ) : (
                                <AlertCircle className="h-4 w-4" />
                            )}
                            {result.data.email_notificacion?.sent
                                ? 'Acceso habilitado y correo enviado correctamente'
                                : 'Acceso habilitado · ⚠️ El correo automático NO se envió'}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="text-slate-600">Email:</div>
                            <div className="font-mono text-slate-900">{result.data.email}</div>
                            <div className="text-slate-600">Tipo:</div>
                            <div><Badge variant="outline" className="text-[10px]">{result.data.plan_tipo}</Badge></div>
                            <div className="text-slate-600">Duración:</div>
                            <div className="text-slate-900">{result.data.duracion_dias} días</div>
                            <div className="text-slate-600">Inicio:</div>
                            <div className="font-mono text-slate-900">{new Date(result.data.otorgado_en).toLocaleString('es-CL')}</div>
                            <div className="text-slate-600">Expira:</div>
                            <div className="font-mono text-emerald-800 font-semibold">{result.data.vence_at_local_cl}</div>
                            <div className="text-slate-600">Otorgado por:</div>
                            <div className="font-mono text-slate-700">{result.data.otorgado_por}</div>
                            {result.data.email_notificacion && (
                                <>
                                    <div className="text-slate-600">Correo automático:</div>
                                    <div className={result.data.email_notificacion.sent ? 'text-emerald-700 font-semibold' : 'text-amber-800 font-semibold'}>
                                        {result.data.email_notificacion.sent
                                            ? '✓ Enviado al docente'
                                            : '✗ NO entregado'}
                                    </div>
                                </>
                            )}
                        </div>
                        {!result.data.email_notificacion?.sent && (
                            <div className="mt-3 rounded-lg bg-white border border-amber-300 p-3 space-y-2">
                                <p className="text-xs text-amber-900 font-semibold">
                                    ¿Por qué no llegó el correo?
                                </p>
                                <p className="text-xs text-amber-900 leading-relaxed">
                                    {result.data.email_notificacion?.reason || 'Resend no aceptó el envío.'}
                                </p>
                                <p className="text-xs text-slate-700 leading-relaxed">
                                    <strong>Solución:</strong> verifica el dominio
                                    {' '}<code className="bg-slate-100 px-1 rounded">portafoliodigitaldocente.cl</code>{' '}
                                    en <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-sky-700 underline">resend.com/domains</a>.
                                    Mientras tanto, puedes avisarle manualmente a <strong>{result.data.email}</strong> que su acceso ya está activo
                                    (Inicio: {new Date(result.data.otorgado_en).toLocaleDateString('es-CL')} · Expira: {result.data.vence_at_local_cl}).
                                </p>
                                <p className="text-xs text-slate-600 italic">
                                    El acceso premium <strong>sí está activo</strong> en la plataforma. La docente solo necesita iniciar sesión para utilizarlo.
                                </p>
                                <div className="flex flex-wrap gap-2 pt-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1.5 text-xs"
                                        onClick={reenviarAlAdmin}
                                        disabled={loading}
                                        data-testid="resend-to-admin-btn"
                                    >
                                        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                                        Enviarme una copia (para reenviar manualmente)
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {result && !result.ok && (
                    <div className="mt-5 rounded-xl border-2 border-rose-200 bg-rose-50 p-4 flex items-start gap-2" data-testid="grant-result-error">
                        <AlertCircle className="h-4 w-4 text-rose-700 mt-0.5" />
                        <p className="text-xs text-rose-900">{result.error}</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
